import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX,
  STORE_CHECKOUT_LIGHT_COLOR_ERROR,
  hsvToHex,
  isLightStoreCheckoutColor,
  normalizeHexColor,
  parseHexToRgb,
  rgbToHsv,
} from './storeCheckoutColorPicker.utils';

import './StoreCheckoutColorPicker.scss';

type DragTarget = 'sv' | 'hue' | null;

interface IStoreCheckoutColorPickerProps {
  disabled?: boolean;
  initialHex?: string;
  /** Preset, custom, and logo-extracted swatches — used to block duplicate adds. */
  existingColors?: readonly string[];
  onAddColor: (hex: string) => void;
  onClose: () => void;
}

const EYEDROPPER_PATH =
  'M410.478 0.0669189C428.336 -0.681556 445.016 4.84632 458.486 16.767C471.806 28.5373 479.871 45.141 480.888 62.887C481.643 75.7273 478.658 88.5108 472.296 99.6898C465.876 110.847 454.693 120.721 445.461 129.939L412.633 162.765C407.053 168.344 399.568 175.413 394.448 181.174C394.708 181.38 394.961 181.595 395.206 181.817C404.908 190.651 405.011 206.469 395.521 215.506C391.138 219.682 386.863 224.521 382.458 228.56C373.806 236.491 366.588 248.282 353.576 248.205C341.063 248.315 334.798 239.15 326.623 231.229C322.473 234.71 314.798 242.857 310.796 246.86L280.126 277.537L183.369 374.295L152.949 404.722C137.757 419.922 121.365 437.685 101.169 446.205C95.2287 448.71 87.9267 449.78 81.6367 451.402C72.0582 453.872 63.5317 460.3 56.2207 466.757C54.0772 468.57 51.997 470.455 49.9835 472.41C42.8347 479.235 37.137 485.812 27.687 476.495C21.1107 469.962 14.5662 463.395 8.01123 456.842C5.91598 454.747 2.10813 451.325 0.956776 448.692C-2.59957 440.562 4.55423 435.26 9.38448 430.052C14.6675 424.36 19.811 418.395 24.0327 411.847C30.079 403.492 30.3737 393.275 33.373 383.935C40.3025 362.357 60.1835 343.887 75.6955 328.372L109.831 294.247L202.784 201.288C218.224 185.848 234.706 169.89 249.793 154.256C247.576 152.266 245.416 149.904 243.311 147.883C237.423 142.224 233.431 137.645 232.966 129.159C232.621 122.896 234.663 116.348 239.103 111.8C243.333 107.363 247.773 103.094 252.096 98.7423C254.601 96.2205 257.126 93.6238 259.666 91.1555C263.051 87.867 266.186 84.1468 270.188 81.6133C273.163 79.7303 277.611 78.5093 281.081 78.3805C289.148 78.0813 294.211 81.1345 299.973 86.373C301.526 85.0905 304.326 82.086 305.903 80.5005L317.411 68.9605L354.853 31.519C362.183 24.1838 370.056 15.525 378.773 10.0611C388.346 4.14669 399.243 0.711769 410.478 0.0669189ZM24.344 444.207C28.2907 448.105 32.8237 452.812 36.8685 456.525C41.5062 452.162 51.321 443.562 56.9067 440.37C64.9227 435.185 72.9495 431.802 82.3065 430.107C92.5237 428.255 100.112 424.762 108.288 418.302C114.689 413.245 120.741 407.697 126.615 402.007C142.136 386.61 157.589 371.09 173.049 355.63L263.833 264.85L296.908 231.77C299.036 229.644 310.821 218.142 311.613 216.544C310.758 214.998 265.373 169.718 264.228 169.135C259.108 173.9 253.928 179.259 248.968 184.225L224.105 209.098L147.713 285.485L97.8797 335.312C86.3205 346.87 75.0105 357.792 64.5057 370.192C52.2585 384.647 52.7957 393.327 47.7867 410.02C44.7285 420.212 32.0595 436.357 24.344 444.207ZM314.438 101.188C319.881 105.917 328.231 114.817 333.576 120.162L373.098 159.661C375.358 161.936 377.581 164.217 379.953 166.377C384.191 162.548 389.623 156.766 393.766 152.614L419.056 127.316L438.341 108.044C442.898 103.491 449.566 97.211 452.981 91.987C457.488 85.1113 460.058 77.147 460.418 68.9333C461.016 56.6003 456.638 44.5438 448.268 35.4668C440.051 26.4325 428.538 21.0943 416.336 20.6595C414.383 20.5955 411.203 20.6543 409.261 20.83C389.821 24.0188 384.771 30.6138 371.513 43.8758L350.996 64.4075L327.438 87.9593C323.441 91.9548 318.013 97.069 314.438 101.188ZM353.798 227.617C355.961 226.39 365.191 216.679 367.571 214.367C371.708 210.177 375.998 206.021 380.091 201.799C380.686 201.185 382.313 199.031 382.178 198.77C380.423 195.42 370.246 185.867 368.093 183.718L343.078 158.697L304.526 120.139C297.948 113.564 291.338 106.895 284.691 100.381C283.591 99.303 283.018 99.1518 281.551 98.9298C277.208 101.586 260.098 120.307 255.211 124.796C254.566 125.388 253.541 127.08 253.216 127.988C253.916 129.846 281.431 156.907 284.886 160.364L334.303 209.728C339.693 215.071 344.963 220.545 350.398 225.84C351.508 226.921 352.293 227.341 353.798 227.617Z';

const applyHexToHsvState = (
  hex: string
): { h: number; s: number; v: number; selectedHex: string } | null => {
  const normalized = normalizeHexColor(hex) ?? STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX;
  const rgb = parseHexToRgb(normalized);
  if (!rgb) {
    return null;
  }
  const [r, g, b] = rgb;
  const hsv = rgbToHsv(r, g, b);
  return { ...hsv, selectedHex: normalized };
};

const StoreCheckoutColorPicker: React.FC<IStoreCheckoutColorPickerProps> = ({
  disabled = false,
  initialHex = STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX,
  existingColors = [],
  onAddColor,
  onClose,
}) => {
  const initialState = applyHexToHsvState(initialHex) ?? {
    h: 210,
    s: 0.72,
    v: 0.92,
    selectedHex: STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX,
  };

  const [hue, setHue] = useState(initialState.h);
  const [saturation, setSaturation] = useState(initialState.s);
  const [value, setValue] = useState(initialState.v);
  const [selectedHex, setSelectedHex] = useState(initialState.selectedHex);
  const [hexInputValue, setHexInputValue] = useState(initialState.selectedHex);
  const [errorMessage, setErrorMessage] = useState('');

  const svPanelRef = useRef<HTMLDivElement>(null);
  const hueTrackRef = useRef<HTMLDivElement>(null);
  const dragTargetRef = useRef<DragTarget>(null);

  const eyedropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  useEffect(() => {
    const next = applyHexToHsvState(initialHex);
    if (next) {
      setHue(next.h);
      setSaturation(next.s);
      setValue(next.v);
      setSelectedHex(next.selectedHex);
      setHexInputValue(next.selectedHex);
    }
  }, [initialHex]);

  const syncHexFromHsv = useCallback(
    (h: number, s: number, v: number) => {
      const hex = hsvToHex(h, s, v);
      setSelectedHex(hex);
      setHexInputValue(hex);
      setErrorMessage((prev) => {
        if (!prev) {
          return prev;
        }
        const normalized = normalizeHexColor(hex);
        if (!normalized || isLightStoreCheckoutColor(normalized)) {
          return prev;
        }
        const isDuplicate = existingColors.some(
          (color) => color.toUpperCase() === normalized.toUpperCase()
        );
        return isDuplicate ? prev : '';
      });
    },
    [existingColors]
  );

  const stopDrag = useCallback(() => {
    dragTargetRef.current = null;
  }, []);

  const updateSvFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = svPanelRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
      const s = rect.width === 0 ? 0 : x / rect.width;
      const v = rect.height === 0 ? 0 : 1 - y / rect.height;
      setSaturation(s);
      setValue(v);
      syncHexFromHsv(hue, s, v);
    },
    [hue, syncHexFromHsv]
  );

  const updateHueFromPointer = useCallback(
    (clientX: number) => {
      const el = hueTrackRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const h = rect.width === 0 ? 0 : (x / rect.width) * 360;
      setHue(h);
      syncHexFromHsv(h, saturation, value);
    },
    [saturation, value, syncHexFromHsv]
  );

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!dragTargetRef.current) {
        return;
      }
      if (dragTargetRef.current === 'sv') {
        updateSvFromPointer(event.clientX, event.clientY);
      } else {
        updateHueFromPointer(event.clientX);
      }
    };

    const onMouseUp = () => {
      stopDrag();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [stopDrag, updateHueFromPointer, updateSvFromPointer]);

  const getSubmitValidationError = useCallback((): string | null => {
    const normalized = normalizeHexColor(hexInputValue) ?? normalizeHexColor(selectedHex);
    if (!normalized) {
      return 'Please enter a valid hex color code (e.g., #FF5733)';
    }
    if (isLightStoreCheckoutColor(normalized)) {
      return STORE_CHECKOUT_LIGHT_COLOR_ERROR;
    }
    const isDuplicate = existingColors.some(
      (color) => color.toUpperCase() === normalized.toUpperCase()
    );
    if (isDuplicate) {
      return 'This color already exists';
    }
    return null;
  }, [existingColors, hexInputValue, selectedHex]);

  const handleHexInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setHexInputValue(inputValue);
    const normalized = normalizeHexColor(inputValue);
    if (normalized) {
      const next = applyHexToHsvState(normalized);
      if (next) {
        setHue(next.h);
        setSaturation(next.s);
        setValue(next.v);
        setSelectedHex(next.selectedHex);
      }
    }
    if (errorMessage) {
      const validationError = getSubmitValidationError();
      if (!validationError) {
        setErrorMessage('');
      }
    }
  };

  const handleUpdate = () => {
    if (disabled) {
      return;
    }
    const validationError = getSubmitValidationError();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    const normalized = normalizeHexColor(hexInputValue) ?? selectedHex;
    setErrorMessage('');
    onAddColor(normalized);
  };

  const handleEyedropperClick = async () => {
    if (disabled || !eyedropperSupported) {
      return;
    }
    try {
      const eyeDropperConstructor = (
        window as unknown as Window &
          Record<'EyeDropper', new () => { open: () => Promise<{ sRGBHex: string }> }>
      ).EyeDropper;
      if (!eyeDropperConstructor) {
        return;
      }
      // eslint-disable-next-line new-cap -- browser EyeDropper API constructor
      const dropper = new eyeDropperConstructor();
      const result = await dropper.open();
      if (result?.sRGBHex) {
        const next = applyHexToHsvState(result.sRGBHex);
        if (next) {
          setHue(next.h);
          setSaturation(next.s);
          setValue(next.v);
          setSelectedHex(next.selectedHex);
          setHexInputValue(next.selectedHex);
        }
      }
    } catch {
      /* user cancelled eyedropper */
    }
  };

  const hueBaseColor = `hsl(${hue}, 100%, 50%)`;
  const isLightSelected = isLightStoreCheckoutColor(selectedHex);

  return (
    <div
      className="store-checkout-color-picker"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <div className="store-checkout-color-picker__anchor">
        <div
          className="store-checkout-color-picker__popover"
          role="dialog"
          aria-label="Add custom color"
        >
          <button
            type="button"
            className="store-checkout-color-picker__popover-close"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Close color picker"
          >
            <svg
              className="store-checkout-color-picker__popover-close-icon"
              viewBox="0 0 10 10"
              width="12"
              height="12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M2.5 2.5 7.5 7.5M7.5 2.5 2.5 7.5"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div
            ref={svPanelRef}
            className="store-checkout-color-picker__sv"
            style={{ backgroundColor: hueBaseColor }}
            onMouseDown={(e) => {
              if (disabled) {
                return;
              }
              e.preventDefault();
              dragTargetRef.current = 'sv';
              updateSvFromPointer(e.clientX, e.clientY);
            }}
            role="presentation"
          >
            <div className="store-checkout-color-picker__sv-overlay store-checkout-color-picker__sv-overlay--white" />
            <div className="store-checkout-color-picker__sv-overlay store-checkout-color-picker__sv-overlay--black" />
            <span
              className="store-checkout-color-picker__sv-thumb"
              style={{
                backgroundColor: selectedHex,
                left: `${saturation * 100}%`,
                top: `${(1 - value) * 100}%`,
              }}
            />
          </div>

          <div className="store-checkout-color-picker__slider-row">
            <button
              type="button"
              className={`store-checkout-color-picker__color-preview ${
                isLightSelected ? 'store-checkout-color-picker__color-preview--light' : ''
              }`}
              style={{ backgroundColor: selectedHex }}
              disabled={disabled || !eyedropperSupported}
              onClick={handleEyedropperClick}
              aria-label="Pick color from screen"
            >
              <svg
                className="store-checkout-color-picker__dropper-icon"
                viewBox="0 0 481 482"
                width="18"
                height="18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  className="store-checkout-color-picker__dropper-icon-path"
                  d={EYEDROPPER_PATH}
                />
              </svg>
            </button>
            <div
              ref={hueTrackRef}
              className="store-checkout-color-picker__hue"
              onMouseDown={(e) => {
                if (disabled) {
                  return;
                }
                e.preventDefault();
                dragTargetRef.current = 'hue';
                updateHueFromPointer(e.clientX);
              }}
              role="presentation"
            >
              <span
                className="store-checkout-color-picker__hue-thumb"
                style={{ left: `${(hue / 360) * 100}%` }}
              />
            </div>
          </div>

          <div className="store-checkout-color-picker__hex-row">
            <input
              type="text"
              className={`store-checkout-color-picker__hex-input ${
                errorMessage ? 'store-checkout-color-picker__hex-input--error' : ''
              }`}
              disabled={disabled}
              value={hexInputValue}
              placeholder="Enter hexcode"
              onChange={handleHexInput}
              aria-invalid={!!errorMessage}
              aria-describedby={errorMessage ? 'store-checkout-color-picker-error' : undefined}
            />
            <button
              type="button"
              className="store-checkout-color-picker__update-btn"
              disabled={disabled}
              onClick={handleUpdate}
            >
              Add
            </button>
          </div>
          {errorMessage ? (
            <p
              id="store-checkout-color-picker-error"
              className="store-checkout-color-picker__error"
              role="alert"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

StoreCheckoutColorPicker.displayName = 'StoreCheckoutColorPicker';

export default StoreCheckoutColorPicker;
